/**
 * Vague 21 — URL Scanner
 *
 * Page principale : formulaire URL + historique + détail d'un scan + actions
 * (créer entité / créer opp / ouvrir 360°).
 *
 * Le scan tourne en arrière-plan via le système de Background Jobs V18.3.
 * L'utilisateur peut continuer à travailler ; le tray l'avertit au done.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Link as LinkIcon, Search, Loader2, Globe, Building2, Sparkles, Shield,
  Plus, ArrowUpRight, AlertTriangle, CheckCircle, Trash2, ChevronRight,
  ExternalLink, Ghost, Network, Eye, RefreshCw,
} from "lucide-react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { startLLMJob } from "@/components/LLMJobsTray";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";

const TYPE_META = {
  entreprise: { label: "Entreprise", color: "#378ADD", icon: Building2 },
  projet: { label: "Projet", color: "#1D9E75", icon: Sparkles },
  article: { label: "Article", color: "#EF9F27", icon: Globe },
  profil: { label: "Profil", color: "#A78BFA", icon: Building2 },
  inconnu: { label: "Inconnu", color: "#71717A", icon: Globe },
};

export default function UrlScanner() {
  const [params] = useSearchParams();
  const [url, setUrl] = useState(params.get("url") || "");
  const [targetType, setTargetType] = useState("auto");
  const [submitting, setSubmitting] = useState(false);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/workspace/url-scanner/scans", { params: { limit: 30 } });
      setScans(r.data?.items || []);
    } catch (e) {
      notify.error(fmtError(e, "Historique indisponible"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!url.trim() || !/^https?:\/\//i.test(url.trim())) {
      notify.error("URL invalide (doit commencer par http:// ou https://)");
      return;
    }
    setSubmitting(true);
    try {
      await startLLMJob("url_scan", { url: url.trim(), target_type: targetType });
      notify.success("Scan lancé en arrière-plan. Le tray vous notifiera au résultat.");
      // refresh historique pour voir bientôt apparaître
      setTimeout(load, 5000);
      setTimeout(load, 15000);
      setTimeout(load, 30000);
    } catch (err) {
      notify.error(fmtError(err, "Lancement impossible"));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer le scan « ${s.title} » ?`)) return;
    try {
      await api.delete(`/workspace/url-scanner/scans/${s.id}`);
      await load();
      if (selected?.id === s.id) setSelected(null);
    } catch (e) { notify.error(fmtError(e, "Suppression impossible")); }
  };

  return (
    <div className="space-y-5" data-testid="url-scanner-page">
      <ThreeQPageHeader endpoint="/workspace/url-scanner/top-priority" />
      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#378ADD]">
          <LinkIcon className="w-3 h-3" /> Hybrid Intelligence v2
        </div>
        <h1 className="noam-display text-2xl font-bold mt-1">URL Scanner</h1>
        <p className="text-zinc-400 text-xs mt-1 max-w-3xl">
          Colle l'URL d'une entreprise, d'un projet, d'un article ou d'un profil. Noam extrait,
          recherche des informations publiques complémentaires, structure la fiche via NOAM,
          et croise avec ton Annuaire / Origination / Risques. Tu peux ensuite créer une entité,
          une opportunité, ou ouvrir la cartographie 360°.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={submit} className="noam-card p-4 space-y-3" data-testid="url-scanner-form">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-[#378ADD] flex-shrink-0" />
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.example.com"
            className="noam-input text-sm flex-1"
            data-testid="url-scanner-input"
            required
          />
          <button type="submit" disabled={submitting} className="btn-primary text-xs" data-testid="url-scanner-submit">
            {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
            Scanner
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Type :</span>
          {["auto", "entreprise", "projet", "article", "profil"].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTargetType(t)}
              className={`text-[10px] px-2 py-0.5 rounded border transition ${
                targetType === t ? "border-[#378ADD]/60 bg-[#378ADD]/15 text-[#378ADD]" : "border-white/10 text-zinc-400"
              }`}
              data-testid={`url-scanner-type-${t}`}
            >
              {t === "auto" ? "Détection auto" : (TYPE_META[t]?.label || t)}
            </button>
          ))}
        </div>
      </form>

      {/* Historique + détail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 noam-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Historique ({scans.length})</div>
            <button onClick={load} className="text-zinc-500 hover:text-zinc-300" data-testid="url-scanner-refresh">
              <RefreshCw className="w-3 h-3" />
            </button>
          </div>
          {loading ? (
            <div className="text-zinc-500 text-xs py-6 text-center"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Chargement…</div>
          ) : scans.length === 0 ? (
            <div className="text-zinc-500 text-xs py-6 text-center">Aucun scan encore.</div>
          ) : (
            <div className="space-y-1 max-h-[600px] overflow-y-auto" data-testid="url-scanner-list">
              {scans.map((s) => {
                const meta = TYPE_META[s.detected_type] || TYPE_META.inconnu;
                const Icon = meta.icon;
                const isActive = selected?.id === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setSelected(s)}
                    className={`w-full text-left p-2 rounded border transition ${
                      isActive ? "border-[#378ADD]/40 bg-[#378ADD]/5" : "border-white/5 bg-white/[0.02] hover:border-white/20"
                    }`}
                    data-testid={`url-scanner-item-${s.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-3 h-3 flex-shrink-0" style={{ color: meta.color }} />
                      <span className="text-xs text-zinc-200 flex-1 truncate">{s.title}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-bold" style={{ background: `${meta.color}1A`, color: meta.color }}>{meta.label}</span>
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-1 truncate">{s.domain || s.url}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <ScanDetail scan={selected} onRefresh={load} onClose={() => setSelected(null)} onDelete={() => remove(selected)} navigate={navigate} />
          ) : (
            <div className="noam-card p-8 text-center text-zinc-500 text-sm">
              Sélectionne un scan pour voir le détail, créer une entité ou une opportunité, et ouvrir la cartographie 360°.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ScanDetail({ scan, onRefresh, onClose, onDelete, navigate }) {
  const s = scan.structured || {};
  const meta = TYPE_META[scan.detected_type] || TYPE_META.inconnu;
  const matches = scan.matches || {};
  const [busy, setBusy] = useState(false);

  const createEntity = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/workspace/url-scanner/scans/${scan.id}/create-entity`, {});
      notify.success(`Entité « ${r.data.name} » créée dans l'Annuaire.`);
      onRefresh();
      navigate(`/app/annuaire`);
    } catch (e) { notify.error(fmtError(e, "Création échouée")); }
    finally { setBusy(false); }
  };
  const createOpp = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/workspace/url-scanner/scans/${scan.id}/create-opportunite`, {});
      notify.success(`Opportunité « ${r.data.titre} » créée.`);
      onRefresh();
      navigate(`/app/origination`);
    } catch (e) { notify.error(fmtError(e, "Création échouée")); }
    finally { setBusy(false); }
  };

  return (
    <div className="noam-card p-5 space-y-4" data-testid="url-scanner-detail">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider" style={{ color: meta.color }}>
            <meta.icon className="w-3 h-3" /> {meta.label} · confiance {scan.confidence}%
          </div>
          <h2 className="text-base font-semibold text-zinc-100 mt-1">{scan.title}</h2>
          <a href={scan.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#378ADD] hover:underline inline-flex items-center gap-1 mt-0.5">
            {scan.url} <ExternalLink className="w-2.5 h-2.5" />
          </a>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button onClick={onDelete} className="text-xs px-2 py-1 rounded border border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid="url-scanner-delete" title="Supprimer">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      {s.summary && <p className="text-xs text-zinc-300">{s.summary}</p>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        {s.sector && <Field label="Secteur" value={s.sector} />}
        {s.country && <Field label="Pays" value={s.country} />}
        {s.entity?.legal_name && <Field label="Raison sociale" value={s.entity.legal_name} />}
        {s.entity?.type && <Field label="Type" value={s.entity.type} />}
        {s.entity?.founded && <Field label="Créée" value={s.entity.founded} />}
        {s.entity?.size_estimate && <Field label="Taille" value={s.entity.size_estimate} />}
      </div>

      {s.signals && (
        <div className="space-y-2" data-testid="url-scanner-signals">
          {s.signals.asymetries_detectees?.length > 0 && (
            <SignalRow label="Asymétries détectées" items={s.signals.asymetries_detectees} color="#EF9F27" icon={Sparkles} />
          )}
          {s.signals.risques_apparents?.length > 0 && (
            <SignalRow label="Risques apparents" items={s.signals.risques_apparents} color="#EF4444" icon={Shield} />
          )}
          {s.signals.ressources_pivot_visibles?.length > 0 && (
            <SignalRow label="Ressources pivot visibles" items={s.signals.ressources_pivot_visibles} color="#1D9E75" icon={CheckCircle} />
          )}
          {s.signals.fantomes_initiaux?.length > 0 && (
            <SignalRow label="Fantômes initiaux" items={s.signals.fantomes_initiaux} color="#A78BFA" icon={Ghost} />
          )}
        </div>
      )}

      {matches.total > 0 && (
        <div className="border-t border-white/10 pt-3" data-testid="url-scanner-matches">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1">
            <Network className="w-3 h-3 text-[#7F77DD]" /> Correspondances internes ({matches.total})
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <MatchBlock label="Annuaire" items={[...(matches.annuaire || []), ...(matches.by_domain || [])]} color="#378ADD" onClick={() => navigate("/app/annuaire")} />
            <MatchBlock label="Opportunités" items={matches.opportunites || []} color="#1D9E75" onClick={() => navigate("/app/origination")} />
            <MatchBlock label="Risques" items={matches.risques || []} color="#EF4444" onClick={() => navigate("/app/risques")} />
          </div>
        </div>
      )}

      {scan.actions_taken && scan.actions_taken.length > 0 && (
        <div className="text-[10px] text-zinc-500 border-t border-white/5 pt-2">
          {scan.actions_taken.length} action(s) effectuée(s) depuis ce scan.
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-white/10">
        <button onClick={createEntity} disabled={busy} className="text-xs px-3 py-1.5 rounded border border-[#378ADD]/40 bg-[#378ADD]/10 text-[#378ADD] hover:bg-[#378ADD]/20 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="url-scanner-create-entity">
          <Plus className="w-3 h-3" /> Créer entité Annuaire
        </button>
        <button onClick={createOpp} disabled={busy} className="text-xs px-3 py-1.5 rounded border border-[#1D9E75]/40 bg-[#1D9E75]/10 text-[#1D9E75] hover:bg-[#1D9E75]/20 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="url-scanner-create-opp">
          <Plus className="w-3 h-3" /> Créer opportunité
        </button>
        <button onClick={onClose} className="text-xs px-3 py-1.5 border border-white/10 text-zinc-400 rounded ml-auto" data-testid="url-scanner-close">
          Fermer
        </button>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="bg-white/[0.02] border border-white/5 rounded p-2">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-zinc-200 truncate mt-0.5">{value}</div>
    </div>
  );
}

function SignalRow({ label, items, color, icon: Icon }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3 h-3" style={{ color }} />
        <span className="text-[10px] uppercase tracking-wider" style={{ color }}>{label} ({items.length})</span>
      </div>
      <ul className="ml-4 space-y-0.5">
        {items.map((it, i) => (
          <li key={i} className="text-[11px] text-zinc-400">• {it}</li>
        ))}
      </ul>
    </div>
  );
}

function MatchBlock({ label, items, color, onClick }) {
  return (
    <button onClick={onClick} className="text-left bg-white/[0.02] border border-white/5 rounded p-2 hover:border-white/20 transition">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider" style={{ color }}>{label}</span>
        <span className="text-[10px] font-bold" style={{ color }}>{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-[10px] text-zinc-600">Aucun</div>
      ) : (
        <ul className="space-y-0.5">
          {items.slice(0, 3).map((it, i) => (
            <li key={i} className="text-[10px] text-zinc-400 truncate">• {it.name || it.titre || it.title}</li>
          ))}
          {items.length > 3 && <li className="text-[10px] text-zinc-600">… +{items.length - 3}</li>}
        </ul>
      )}
    </button>
  );
}
